import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Package,
  Clock,
  ChevronRight,
  ChevronDown,
  ShoppingBag,
  AlertCircle,
  Truck,
  MapPin,
  Store,
  Calendar,
  CreditCard,
} from "lucide-react";
import { format } from "date-fns";

const STORE_CONFIG = {
  coles: { label: "Coles", bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500" },
  woolworths: { label: "Woolworths", bg: "bg-green-50", border: "border-green-200", text: "text-green-700", dot: "bg-green-500" },
  chemist_warehouse: { label: "Chemist Warehouse", bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500" },
};

const getStoreKey = (storeName) => {
  const s = (storeName || "").toLowerCase();
  if (s.includes("woolworth")) return "woolworths";
  if (s.includes("coles")) return "coles";
  if (s.includes("chemist")) return "chemist_warehouse";
  return null;
};

const getStatusConfig = (status) => {
  const map = {
    pending: { color: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending" },
    confirmed: { color: "bg-blue-100 text-blue-800 border-blue-200", label: "Confirmed" },
    processing: { color: "bg-purple-100 text-purple-800 border-purple-200", label: "Processing" },
    ready_for_delivery: { color: "bg-blue-100 text-blue-800 border-blue-200", label: "Confirmed" },
    out_for_delivery: { color: "bg-indigo-100 text-indigo-800 border-indigo-200", label: "Out for Delivery" },
    delivered: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Delivered" },
    cancelled: { color: "bg-red-100 text-red-800 border-red-200", label: "Cancelled" },
  };
  return map[status] || { color: "bg-slate-100 text-slate-800", label: status };
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const hasShownToast = React.useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      if (!hasShownToast.current) {
        hasShownToast.current = true;
        toast.error("Please login to view your orders");
        navigate("/");
      }
      setLoading(false);
      return;
    }

    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Only show Confirmed, Delivered, and Cancelled orders to customers
        // "ready_for_delivery" orders stay visible but display as "Confirmed"
        const visibleStatuses = ["confirmed", "ready_for_delivery", "delivered", "cancelled"];
        const filtered = (response.data.orders || []).filter(
          (o) => visibleStatuses.includes(o.order_status)
        );
        setOrders(filtered);
      } catch (error) {
        toast.error("Failed to load orders");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated, token, navigate]);

  // Group items by store for an order
  const groupByStore = (items) => {
    const groups = {};
    for (const item of items || []) {
      const retailer = item.retailer || item.store_name || item.store_id || "Unknown";
      const storeKey = getStoreKey(retailer) || retailer;
      if (!groups[storeKey]) groups[storeKey] = [];
      groups[storeKey].push(item);
    }
    return groups;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
                <div className="h-5 bg-slate-200 rounded w-1/4 mb-3" />
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-1/3" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="no-orders">
          <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">No Orders Yet</h1>
          <p className="text-slate-500 mb-6 text-sm">Start shopping to see your order history here</p>
          <Button onClick={() => navigate("/products")} data-testid="browse-btn">
            Browse Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="orders-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6" data-testid="orders-title">My Orders</h1>

        <div className="space-y-4">
          {orders.map((order) => {
            const status = getStatusConfig(order.order_status);
            const storeGroups = groupByStore(order.items);
            const isExpanded = expandedOrder === order.id;
            const delivery = order.delivery_details || {};

            return (
              <div key={order.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-testid={`order-card-${order.id}`}>
                {/* Order Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                  data-testid={`order-header-${order.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                        <Package className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 text-sm">
                            Order {order.order_number || `BC-${order.id?.slice(0, 6).toUpperCase()}`}
                          </h3>
                          <Badge className={`${status.color} text-[10px] h-5`} data-testid={`order-status-${order.order_number || order.id}`}>
                            {status.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {order.created_at && format(new Date(order.created_at), "dd MMM yyyy, h:mm a")}
                          <span className="mx-1">-</span>
                          {(order.items || []).length} item(s) from {Object.keys(storeGroups).length} store(s)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-slate-900" data-testid={`order-total-${order.id}`}>
                        ${(order.total || 0).toFixed(2)}
                      </span>
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>

                  {/* Store dots row */}
                  <div className="flex items-center gap-2 mt-2 pl-[52px]">
                    {Object.keys(storeGroups).map((storeKey) => {
                      const cfg = STORE_CONFIG[storeKey];
                      if (!cfg) return null;
                      return (
                        <span key={storeKey} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${cfg.bg} ${cfg.border} ${cfg.text} border`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                          {cfg.label} ({storeGroups[storeKey].length})
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-slate-100">
                    {/* Delivery Info */}
                    {delivery.address && (
                      <div className="px-4 py-3 bg-slate-50 flex flex-wrap gap-4 text-xs text-slate-600">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {delivery.address}, {delivery.suburb} {delivery.postcode}
                        </span>
                        {delivery.delivery_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(delivery.delivery_date), "dd MMM yyyy")} {delivery.delivery_time || ""}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Items grouped by store */}
                    <div className="divide-y divide-slate-100" data-testid={`order-items-${order.id}`}>
                      {Object.entries(storeGroups).map(([storeKey, items]) => {
                        const cfg = STORE_CONFIG[storeKey] || { label: storeKey, bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", dot: "bg-slate-400" };
                        const storeSubtotal = items.reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1), 0);

                        return (
                          <div key={storeKey} data-testid={`store-group-${storeKey}`}>
                            {/* Store Header */}
                            <div className={`px-4 py-2 ${cfg.bg} border-l-4 ${cfg.border} flex items-center justify-between`}>
                              <span className={`flex items-center gap-1.5 text-xs font-semibold ${cfg.text}`}>
                                <Store className="w-3 h-3" />
                                {cfg.label}
                              </span>
                              <span className={`text-xs font-medium ${cfg.text}`}>
                                ${storeSubtotal.toFixed(2)}
                              </span>
                            </div>

                            {/* Store Items */}
                            {items.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                                {item.image_url ? (
                                  <img
                                    src={item.image_url}
                                    alt={item.product_name}
                                    className="w-10 h-10 rounded-lg object-cover border border-slate-200"
                                    onError={(e) => { e.target.style.display = "none"; }}
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                    <Package className="w-4 h-4 text-slate-400" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-slate-900 truncate">{item.product_name}</p>
                                  <p className="text-[11px] text-slate-500">Qty: {item.quantity || 1}</p>
                                </div>
                                <span className="text-sm font-medium text-slate-900 tabular-nums">
                                  ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>

                    {/* Order Footer */}
                    <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5 text-xs text-slate-500">
                          <div className="flex items-center gap-4">
                            <span>Subtotal: ${(order.subtotal || 0).toFixed(2)}</span>
                            <span>Fee: ${(order.convenience_fee || 0).toFixed(2)}</span>
                            <span>Delivery: ${(order.delivery_fee || 0).toFixed(2)}</span>
                          </div>
                          {order.payment_status && (
                            <span className="flex items-center gap-1">
                              <CreditCard className="w-3 h-3" />
                              Payment: {order.payment_status === "paid" ? "Paid" : "Pending"}
                            </span>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.order_number || order.id}`); }}
                          data-testid={`view-order-${order.order_number || order.id}`}
                        >
                          View Details
                          <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
